/**
 * All changes after [behavior_store@ab9709a52d](https://github.com/optimizely/optimizely/blob/ab9709a52d9b2579c72d436556b1d2c3c4276a6c/src/www/js/client/behavior_store.js)
 * made directly to client-js.
 *
 * This store 'contains' click/pageview/custom events (module:behavior/event~Event) that have
 * occurred on the local origin. Why scare quotes 'contains'? See the next paragraph.
 * It's seeded with data from the local StorageKeys.{EVENTS,EVENT_QUEUE} localStorage items.
 * Click/pageview/custom events submitted to it are queued; events are only sessionized
 * and integrated into the store when #getEvents is called.
 *
 * At a lower level: this is a PSEUDO-store that serves as the sole interface to local behavior
 * event storage; it should be the ONLY module that dispatches actionTypes.SET_VISITOR_EVENTS!
 * This module adds a layer of compression and lazy sessionization support atop the
 * VisitorEventsStore's `events` state property.
 *
 * To aid in the implementation of this module, a helper store is used called the
 * VisitorEventsManagerStore.
 *
 * @module
 */

var _ = require('optimizely-client-fn');
var actionTypes = require('../constants/action_types');
var Cookie = require('../utils/cookie');
var DateEnv = require('../env/date');
var eventCategoryEnums = require('../constants/event_category');
var Flux = require('../flux');
var Logger = require('../utils/logger');
var Policy = require('./policy');
var Visitor = require('../visitor');

// Add private members to "Store" so they are accessible for stubbing;
// add public members to exports so they are documented correctly.
var Store = exports;

var BehaviorEvent = require('./event').Event;
var enums = require('./enums');
var constants = require('../constants/enums');
var EventBase = require('./event').EventBase;
var Policy = require('./policy');

var di = require('../di');
var VisitorEventsStore = di.get('stores/visitor_events');
var VisitorEventsManagerStore = di.get('stores/visitor_events_manager');

/**
 * Keys in a compact event.
 * @enum {string}
 * @private
 */
var COMPACT_KEYS = {
  EVENTBASE: 'eb',
  HASH: 'h',
  TIMEBASE: 'tb',
  TIMESTAMPS: 'ts',
  DELTA: 'd',
  INDEX: 'i'
};

var MAX_EVENTS = 1000;

function setEvents(events) {
  Flux.dispatch(actionTypes.SET_VISITOR_EVENTS, events);
}

function setBaseMap(baseMap) {
  Flux.dispatch(actionTypes.UPDATE_BEHAVIOR_STORE, {
    key: 'baseMap',
    value: baseMap,
  });
}

function setEventQueue(eventQueue) {
  Flux.dispatch(actionTypes.UPDATE_BEHAVIOR_STORE, {
    key: 'eventQueue',
    value: eventQueue,
  });
}

function setLastEvent(lastEvent) {
  Flux.dispatch(actionTypes.UPDATE_BEHAVIOR_STORE, {
    key: 'lastEvent',
    value: lastEvent,
  });
}

function setCleared(cleared) {
  Flux.dispatch(actionTypes.UPDATE_BEHAVIOR_STORE, {
    key: 'cleared',
    value: cleared,
  });
}

function setInitialized() {
  Flux.dispatch(actionTypes.UPDATE_BEHAVIOR_STORE, {
    key: 'initialized',
    value: true,
  });
}

function getEventsFromStore() {
  return VisitorEventsStore.getEvents();
}

function getBaseMap() {
  return VisitorEventsManagerStore.getBaseMap();
}

function getEventQueue() {
  return VisitorEventsManagerStore.getEventQueue();
}

function getLastEvent() {
  return VisitorEventsManagerStore.getLastEvent();
}

function getCleared() {
  return VisitorEventsManagerStore.getCleared();
}

function getInitialized() {
  return VisitorEventsManagerStore.getInitialized();
}

/**
 * Initializes the behavior store.
 * @param {BehaviorVerboseEvent[]|BehaviorCompactEvent[]} storedEvents
 * @param {BehaviorVerboseEvent[]|BehaviorCompactEvent[]} queuedEvents
 */
exports.initialize = function(storedEvents:BehaviorVerboseEvent[]|BehaviorCompactEvent[], queuedEvents:BehaviorVerboseEvent[]|BehaviorCompactEvent[]) {
  if (__INVARIANT__) {
    require('../utils/invariant').isType('BehaviorStoredEvents', storedEvents);
    require('../utils/invariant').isType('BehaviorStoredEvents', queuedEvents);
  }

  if (getInitialized()) {
    // Already initialized.
    return;
  }

  Store.__populatePersistedEvents(storedEvents, queuedEvents);

  // Update last event so new events can update their session id.
  var events = getEventsFromStore();
  if (events.length > 0) {
    setLastEvent(events[events.length - 1]);
  }

  // Events in queue are more recent; update lastEvent if any queued events present.
  var eventQueue = getEventQueue();
  if (eventQueue.length > 0) {
    setLastEvent(eventQueue[eventQueue.length - 1]);
  }

  setInitialized();
};

/**
 * Adds event to behavioral store.
 * @param {Object} event
 * @param {string} event.name
 * @param {string} [event.type]
 * @param {string} [event.category]
 */
exports.addEvent = function(event:any) {
  Logger.debug('Behavior store: adding event', event);

  if (__INVARIANT__) {
    if (!getInitialized()) {
      throw new Error('behavior.addEvent: Store not initialized');
    }
  }

  var behaviorEvent = Store.__makeEvent(event);

  setLastEvent(behaviorEvent);
  setEventQueue(getEventQueue().concat(behaviorEvent));

  Policy.reindexIfNecessary(getLastEvent(), getEventsFromStore(), getEventQueue());

  Store.__persistEventQueue(getEventQueue());

  if (behaviorEvent.t) {
    setSessionCookie(behaviorEvent.t);
  }
};

function setSessionCookie(timestamp: number) {
  try {
    let current = Number(Cookie.get(constants.COOKIES.OPTIMIZELY_SESSION, false));

    if (isNaN(current) || current !== 0) {
      let value = current ? (Policy.isTimestampInSameSession(timestamp, current) ? timestamp : 0) : timestamp;

      Cookie.set(
        constants.COOKIES.OPTIMIZELY_SESSION,
        value.toString(),
        {
          path: '/',
          encodeValue: false,
        },
        true
      );
    }
  } catch (e) {
    Logger.error('Error setting session cookie', e);
  }
}

/**
 * Returns events in the store.
 * Side effects: Before returning, it resolves the queue by merging it and persisting
 * the result. The queue is then set to empty.
 *
 * Note: THIS FUNCTION IS NOT "THREAD-SAFE" due to flawed cache invalidation.
 * A quasi-postcondition of this function is that the returned value is consistent with the contents
 * of localStorage. Why "quasi"? Another context on the same origin (such as another open tab)
 * could call addEvent() and getEvents(), causing localStorage to diverge. At that point, calling this
 * function in the original context would wrongly still return the old in-memory events, not the
 * updated set of persisted events.

 * @returns {Array.<module:behavior/event~Event>}
 *        The post-merge result.
 */
exports.getEvents = function(): import('core/behavior/event').Event[] {
  if (__INVARIANT__) {
    if (!getInitialized()) {
      throw new Error('behavior.getEvents: Store not initialized');
    }
  }

  // As events are added via exports.addEvent, they're queued in memory + on disk, NOT added.
  // Here the queue is lazily merged into the in-memory event collection (within mergeQueueAndCapTotalEvents).
  // This is a means of batching sessionization, which is assumed to be relatively expensive.
  if (getEventQueue().length > 0) {
    if (mergeQueueAndCapTotalEvents()) {
      Policy.sessionize(getEventsFromStore());
    }

    // Events and event queue were merged; persist the new state.
    Store.__persistEvents(getEventsFromStore());
    Store.__persistEventQueue(getEventQueue());  // Should be empty
  }

  return getEventsFromStore();
};

exports.getEventCount = function() {
  return getEventQueue().length + getEventsFromStore().length;
};

/**
 * Populates the events from persistent storage, migrates events, if required,
 * and sessionizes events.
 * @param {BehaviorCompactEvent[]|module:behavior/event~Event[]} storedEvents
 * @param {BehaviorCompactEvent[]|module:behavior/event~Event[]} queuedEvents
 * @private
 */
Store.__populatePersistedEvents = function(storedEvents:BehaviorCompactEvent[]|import('core/behavior/event').Event[], queuedEvents:BehaviorCompactEvent[]|import('core/behavior/event').Event[]) {
  (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').time('populatePersistedEvents');
  if (Store.__migrateEventsAndUpdateStore(storedEvents, queuedEvents)) {
    // Migrations were applied, or stored event queue was found; persist events.
    Store.__persistEvents(getEventsFromStore());
    Store.__persistEventQueue(getEventQueue());
  }

  // Sessionize.
  Policy.sessionize(getEventsFromStore());
  (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').timeEnd('populatePersistedEvents');
};

/**
 * Migrates events to latest format and updates store events and baseMap.
 * @param {Array.<Object.<*>>} events Events to migrate.
 * @param {Array.<Object.<*>>} eventQueue Event queue to migrate.
 * @returns {boolean} Whether any migrations were applied.
 * @private
 */
Store.__migrateEventsAndUpdateStore = function(events:Record<string, any>[], eventQueue:Record<string, any>[]): boolean {
  if (events.length === 0 && eventQueue.length === 0) {
    setEvents([]);
    setEventQueue([]);
    return false;
  }

  // Are events already in de-duped format?
  var wasMigrated = false;
  var firstEvent = events[0] || eventQueue[0];
  if (!(COMPACT_KEYS.EVENTBASE in firstEvent)) {
    // No, migrate them, and remember to persist them.
    wasMigrated = true;
    setEvents(Store.__migrateFromVerboseFormat(events));
    setEventQueue(Store.__migrateFromVerboseFormat(eventQueue));
  } else {
    setEvents(Store.__unCompact(
      /** @type{Array.<BehaviorCompactEvent>} */(events)));
    setEventQueue(Store.__unCompact(
      /** @type{Array.<BehaviorCompactEvent>} */(eventQueue)));
  }

  // Merge events.
  if (getEventQueue().length > 0) {
    mergeQueueAndCapTotalEvents();
    wasMigrated = true;
  }

  // NOTE: Events must be de-duped to save space before persisting them.
  setEvents(Store._updateBaseMapAndMaybeDedupe(getEventsFromStore()));

  // Apply any required migrations to the event bases.
  if (Store._migrateEventBasesAndUpdateStore()) {
    wasMigrated = true;
  }
  // TODO(ajith): Add other migrations, such as obfuscation, purging.

  return wasMigrated;
};

/**
 * Migrates events from verbose format to new format with potentially duplicate
 * event bases; they will be de-duped before persisting.
 * @param {Array.<BehaviorVerboseEvent>} events Events to migrate.
 * @returns {Array.<module:behavior/event~Event>} Migrated events.
 * @private
 */
Store.__migrateFromVerboseFormat = function(events:BehaviorVerboseEvent[]): import('core/behavior/event').Event[] {
  // TODO(tyler): relies on reference
  var migratedEvents = [];
  for (var i = 0; i < events.length; i++) {
    var event = events[i];
    var eventBase = Store.__makeEventBase(event);
    migratedEvents[i] = new BehaviorEvent(eventBase, /** @type{number} */(event[enums.FIELDS.TIME]));
  }

  return migratedEvents;
};

/**
 * Applies any required migrations to the event bases.
 * @returns {boolean} Whether any migrations were applied.
 * @private
 *
 * TODO(tyler): fix tests so this isn't required to be exported
 */
Store._migrateEventBasesAndUpdateStore = function(): boolean {
  var wasMigrated = false;
  // E.g. Migrate events whose name field was set to goal names to use goal ids.
  // Goal ids can disambiguate between goals with the same name, and can also
  // obfuscate the event.
  var eventBases = Store.__getAllEventBases();
  if (Policy.applyMigrations(eventBases)) {
    wasMigrated = true;

    // Event bases were changed (hashes were recomputed); rebuild baseMap.
    setBaseMap({});
    setEvents(Store._updateBaseMapAndMaybeDedupe(getEventsFromStore()));
    // Unlikely that the eventQueue is populated at this point, but de-dupe
    // to be safe.
    setEventQueue(Store._updateBaseMapAndMaybeDedupe(getEventQueue()));
  }

  return wasMigrated;
};

/**
 * Merges the event queue into events and caps total events.
 * @returns {boolean} Whether events were capped.
 * @private
 */
function mergeQueueAndCapTotalEvents(): boolean {
  var merged = getEventsFromStore().concat(getEventQueue());
  var capped = false;

  if (merged.length > MAX_EVENTS) {
    // Get the last MAX_EVENTS elements of all events.
    merged = merged.slice(-MAX_EVENTS);
    capped = true;
  }

  setEvents(merged);
  setEventQueue([]);
  return capped;
}

/**
 * Returns a timestamp.
 * @private
 * @returns {number} Timestamp.
 */
Store.__getTime = function(): number {
  return DateEnv.now();
};

/**
 * Makes a behavior event.
 * @param {BehaviorEventSpec} event
 * @returns {module:behavior/event~Event}
 * @private
 */
Store.__makeEvent = function(event:BehaviorEventSpec): import('core/behavior/event').Event {
  if (__INVARIANT__) {
    require('../utils/invariant').isType('BehaviorEventSpec', event);
  }

  var name = event.name,
      type = /** @type{string} */(event.type || 'default'),
      category = /** @type{string} */(event.category || eventCategoryEnums.OTHER);

  var revenue;
  var tags = event.tags || {};

  if (event.revenue) {
    revenue = event.revenue;
  }

  // TODO(ajith): Sanitize tags to ensure they satisfy number and size policy.

  var eventBase = new EventBase(name, type, category, tags, revenue);
  eventBase = Store.__findOrSetEventBase(eventBase);

  var timestamp = Store.__getTime();
  var behaviorEvent = new BehaviorEvent(eventBase, timestamp, -1);

  // updateSessionIndex must always be called at the same time as updateSessionId
  // TODO: Remove sessionId (CJS-2049)
  Policy.updateSessionId(getLastEvent(), behaviorEvent);
  Policy.updateSessionIndex(getLastEvent(), behaviorEvent);

  return behaviorEvent;
};

/**
 * Updates the baseMap and de-dupes event bases, if required. Updates events in-place.
 * @param {Array.<module:behavior/event~Event>} events
 * @returns {Array.<module:behavior/event~Event>}
 * @private
 *
 * TODO(tyler): update tests to not require exporting this
 */
Store._updateBaseMapAndMaybeDedupe = function(events:import('core/behavior/event').Event[]): import('core/behavior/event').Event[] {
  for (var i = 0; i < events.length; i++) {
    events[i].eventBase = Store.__findOrSetEventBase(events[i].eventBase);
  }

  // return modified events
  return events;
};

/**
 * Persists events to store.
 * @param {Array.<module:behavior/event~Event>} events
 * @private
 */
Store.__persistEvents = function(events:import('core/behavior/event').Event[]) {
  var compactEvents = Store.__compact(events);
  Visitor.persistBehaviorEvents(compactEvents);
};

/**
 * Persists queued events to store.
 * @param {Array.<module:behavior/event~Event>} events
 * @private
 */
Store.__persistEventQueue = function(events:import('core/behavior/event').Event[]) {
  var compactEvents = Store.__compact(events);
  Visitor.persistBehaviorEventQueue(compactEvents);
};

/**
 * Clear the event store.
 *
 * @private
 */
Store.__clear = function() {
  if (getCleared()) {
    return;
  }
  setEvents([]);
  setEventQueue([]);
  Store.__persistEvents(getEventsFromStore());
  Store.__persistEventQueue(getEventQueue());
  setBaseMap({});
  setLastEvent(null);
  setCleared(true);
};


/**
 * Returns eventBase if already in store, else stores it.
 * @param {module:behavior/event~EventBase} eventBase
 * @returns {module:behavior/event~EventBase} EventBase if found in store, otherwise input value.
 * @private
 */
Store.__findOrSetEventBase = function(eventBase:import('core/behavior/event').EventBase): import('core/behavior/event').EventBase {
  var baseHash = eventBase.hash();
  var baseMap = getBaseMap();
  var candidates = baseMap[baseHash];
  if (_.isUndefined(candidates)) {
    baseMap[baseHash] = [ eventBase ];
    setBaseMap(baseMap);
    return eventBase;
  }

  // Search in the candidates list.
  for (var i = 0; i < candidates.length; i++) {
    if (eventBase.equals(candidates[i])) {
      return candidates[i];
    }
  }

  // No equal eventBase found in candidate list; store it.
  candidates.push(eventBase);

  // Register the change in Flux
  setBaseMap(baseMap);
  return eventBase;
};

/**
 * Returns all event bases stored in baseMap.
 * @returns {module:behavior/event~EventBase[]} All event bases stored in baseMap.
 * @private
 */
Store.__getAllEventBases = function(): import('core/behavior/event').EventBase[] {
  var eventBases = [];
  var baseMap = getBaseMap();
  for (var baseHash in baseMap) {
    if (!baseMap.hasOwnProperty(baseHash)) {
      continue;
    }
    eventBases = eventBases.concat(baseMap[baseHash]);
  }

  return eventBases;
};

/**
 * Compacts events into a format suitable for serializing.
 *
 * E.g. Suppose we have 5 events, 2 of them sharing one EventBase eb1, and
 * the other 3 sharing EventBase eb2.
 * events = [{
 *   eventBase: eb1,
 *   t: 1234
 * }, {
 *   eventBase: eb2,
 *   t: 1236
 * }, {
 *   eventBase: eb1,
 *   t: 1237
 * }, {
 *   eventBase: eb2,
 *   t: 1238
 * }, {
 *   eventBase: eb2,
 *   t: 1239
 * }]
 *
 * Store.__compact(events) will return the following compact representation.
 * [{
 *    eb: eb1,
 *     h: <eb1's hash>
 *    tb: 1234,
 *    ts: [{d: 0, i: 0}, {d: 3, i: 2}]
 * }, {
 *    eb: eb2,
 *     h: <eb2's hash>
 *    tb: 1236,
 *    ts: [{d: 0, i: 1}, {d: 2, i: 3}, {d: 3, i: 4}]
 * }]
 *
 * @param {module:behavior/event~Event[]} events
 * @returns {Array.<BehaviorCompactEvent>} Compact representation of events.
 * @private
 */
Store.__compact = function(events:import('core/behavior/event').Event[]): BehaviorCompactEvent[] {
  /**
   * Returns a CompactEB object with fields taken from event.
   * @param {module:behavior/event~Event} verboseEvent
   * @returns {CompactEB}
   */
  var extractCompactEB = function(verboseEvent:import('core/behavior/event').Event): CompactEB {
    var obj = {} as any;
    obj[enums.FIELDS.NAME] = verboseEvent.getValueOrDefault([enums.FIELDS.NAME]);
    obj[enums.FIELDS.TYPE] = verboseEvent.getValueOrDefault([enums.FIELDS.TYPE]);

    var category = verboseEvent.getValueOrDefault([enums.FIELDS.CATEGORY]);
    if (!_.isUndefined(category)) {
      obj[enums.FIELDS.CATEGORY] = category;
    }

    var revenue = verboseEvent.getValueOrDefault([enums.FIELDS.REVENUE]);
    if (!_.isUndefined(revenue)) {
      obj[enums.FIELDS.REVENUE] = revenue;
    }

    var options = verboseEvent.getValueOrDefault([enums.FIELDS.OPTIONS]);
    if (!_.isUndefined(options)) {
      obj[enums.FIELDS.OPTIONS] = options;
    }
    return /** @type{CompactEB} */(obj);
  };

  var keys = COMPACT_KEYS;
  var compactEvents = [];
  // When we first encounter a particular EventBase, we store its corresponding
  // CompactEvent in compactEvents, and save its index temporarily in a field
  // called '_idx_' within EventBase. When we process subsequent events that
  // share this EventBase, we can quickly locate the corresponding CompactEvent
  // using the value of field '_idx_'. We delete '_idx_' after all events are
  // processed.
  var locationKey = '_idx_';
  for (var i = 0; i < events.length; i++) {
    var event = events[i];
    var eventBase = event.eventBase;

    var compactEvent,
        timestamp;
    // Was this eventBase seen before? If so, the value of locationKey is its
    // index within compactEvents.
    if (!eventBase.hasOwnProperty(locationKey)) {
      // This is the first time an event with this eventBase is seen.
      compactEvent = {} as any;
      compactEvent[keys.EVENTBASE] = extractCompactEB(event);
      compactEvent[keys.HASH] = eventBase.hash();
      // Record the timestamp of the first event.
      compactEvent[keys.TIMEBASE] = event[enums.FIELDS.TIME];
      timestamp = {} as any;
      // The delta time from the the first event's timestamp. Zero because this is the first event.
      timestamp[keys.DELTA] = 0;
      timestamp[keys.INDEX] = i; // Used to find time order when un-compacting.
      compactEvent[keys.TIMESTAMPS] = [ /** @type{CompactTS} */(timestamp) ];
      compactEvents.push(compactEvent);

      // Remember the index of this event base so that subsequent events with
      // this base can be stored quickly.
      eventBase[locationKey] = compactEvents.length - 1;
    } else {
      // This eventBase was seen earlier.
      compactEvent = compactEvents[eventBase[locationKey]];

      // Store the delta time to the timebase (first timestamp with this eventBase).
      var delta = event[enums.FIELDS.TIME] - (compactEvent[keys.TIMEBASE] || 0);
      timestamp = {} as any;
      timestamp[keys.DELTA] = delta;
      // Store the index of the event; it will be used when rebuilding the
      // un-compacted event list.
      timestamp[keys.INDEX] = i;
      compactEvent[keys.TIMESTAMPS].push(/** @type{CompactTS} */(timestamp));
    }
  }

  // Cleanup locationKey from events.
  for (i = 0; i < events.length; i++) {
    delete events[i].eventBase[locationKey];
  }

  return compactEvents;
};

/**
 * Helper to recreate an EventBase object from a JSON object.
 * @param {BehaviorVerboseEvent|CompactEB} obj
 * @param {number} [hash] Optional persisted hash of event base.
 * @returns {module:behavior/event~EventBase}
 * @private
 */
Store.__makeEventBase = function(obj:BehaviorVerboseEvent|CompactEB, hash?:number): import('core/behavior/event').EventBase {
  var eventBase = new EventBase(
    obj[enums.FIELDS.NAME],
    obj[enums.FIELDS.TYPE],
    obj[enums.FIELDS.CATEGORY],
    obj[enums.FIELDS.OPTIONS],
    obj[enums.FIELDS.REVENUE]
  );

  if (!_.isUndefined(hash)) {
    eventBase.setHash(/** @type{number} */(hash));
  }

  return eventBase;
};

/**
 * Un-compacts events from a representation suitable for serializing into one
 * suitable for rule evaluation.
 * @param {BehaviorCompactEvent[]} compactEvents Compact representation of events.
 * @returns {module:behavior/event~Event[]} events
 * @private
 */
Store.__unCompact = function(compactEvents:BehaviorCompactEvent[]): import('core/behavior/event').Event[] {
  var keys = COMPACT_KEYS;
  var events = [];
  for (var i = 0; i < compactEvents.length; i++) {
    var compactEvent = compactEvents[i];
    var eventBase = Store.__makeEventBase(compactEvent[keys.EVENTBASE], compactEvent[keys.HASH]);
    var timeBase = compactEvent[keys.TIMEBASE];
    var timestamps = compactEvent[keys.TIMESTAMPS];
    for (var j = 0; j < timestamps.length; j++) {
      var timestamp = timestamps[j];
      var event = new BehaviorEvent(eventBase, timeBase + timestamp[keys.DELTA]);
      var index = timestamp[keys.INDEX];
      events[index] = event;
    }
  }

  return events;
};

/**
 * De-serializes events from a serialized compact representation into one
 * suitable for rule evaluation.
 * @param {BehaviorCompactEvent[]} compactEvents Compact representation of events.
 * @returns {module:behavior/event~Event[]} events
 */
exports.deserialize = function(compactEvents:BehaviorCompactEvent[]): import('core/behavior/event').Event[] {
  return Store.__unCompact(compactEvents);
};

/**
 * Returns all events in the list of lists, sorted on time, and sessionized.
 * @param {Array.<Array.<module:behavior/event~Event>>} eventsLoL - list of lists of Events, not necessarily sorted
 * @returns {Array.<module:behavior/event~Event>} Merged, sorted, and sessionized events.
 */
exports.mergeAllEvents = function(eventsLoL:import('core/behavior/event').Event[][]): import('core/behavior/event').Event[] {
  var result = [].concat.apply([], eventsLoL);
  result.sort(Policy.sessionSortPredicate);
  Policy.sessionize(result);

  return result;
};

if (__TEST__) {
  exports.COMPACT_KEYS = COMPACT_KEYS;
  exports.MAX_EVENTS = MAX_EVENTS;
  exports.getBaseMap = getBaseMap;
  exports.getEventQueue = getEventQueue;
  exports.getLastEvent = getLastEvent;
  exports.getCleared = getCleared;
  exports.getInitialized = getInitialized;
  exports.mergeQueueAndCapTotalEvents = mergeQueueAndCapTotalEvents;
  exports.setInitialized = setInitialized;
  exports.setBaseMap = setBaseMap;
  exports.setEventQueue = setEventQueue;
  exports.setLastEvent = setLastEvent;
  exports.setEvents = setEvents;
}
